import React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AutoAwesome as AiIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { SIDEBAR_MODULE_TITLE, SIDEBAR_NAV_ITEMS } from '../../constants/sidebarNavConfig';
import { useAiChatPanel } from '../../contexts/AiChatPanelContext';

const RAIL_WIDTH = 80; // ความกว้างแถบไอคอนซ้ายของ MainSidebar (PRIMARY_RAIL_WIDTH)
// สีพื้นเดียวกับแถบไอคอนซ้ายของ MainSidebar (BRAND_RAIL_BG) — อ้าง CSS variable ตัวเดียวกันจะได้เปลี่ยนตาม org/theme.json
const BRAND_RAIL_BG = 'rgb(var(--color-primary-900))';

// หาชื่อหน้าที่เปิดอยู่จาก path ปัจจุบัน — เทียบกับ href ของเมนู sidebar (ตรงทั้งหมดหรือเป็นหน้าย่อยของ href นั้น เช่น
// /finance/expenses/123) เลือกอันที่ href ยาวที่สุด ไม่ตรงกับเมนูไหนเลย = ไม่แสดงอะไร
const getPageLabel = (pathname) => {
  // เทียบแบบไม่สนตัวพิมพ์ใหญ่/เล็ก — บาง route ในระบบเป็น /Customer/..., /finance/Payment ส่วน config เป็นตัวเล็ก
  const path = (pathname.replace(/\/+$/, '') || '/').toLowerCase();
  let best = null;
  const consider = (rawHref, title, moduleName) => {
    const href = rawHref?.toLowerCase();
    if (!href || (path !== href && !path.startsWith(`${href}/`))) {
      return;
    }
    if (!best || href.length > best.href.length) {
      best = { href, title, moduleName };
    }
  };
  SIDEBAR_NAV_ITEMS.forEach((item) => {
    // โมดูลที่มีเมนูย่อย: href/title ของแถวหลักเป็นแค่ชื่อกลุ่ม (href ซ้ำกับเมนูย่อยตัวแรก เช่น /finance/expenses
    // ชื่อ "การเงิน") ห้ามเอามาแข่ง ไม่งั้นชนะเมนูย่อยจริงที่ href เท่ากัน
    if (item.sub?.length) {
      item.sub.forEach((s) => consider(s.href, s.title, item.name));
    } else {
      consider(item.href, item.title, item.name);
    }
  });

  if (best) {
    return best;
  }

  // หน้าที่ไม่ได้อยู่ในเมนู sidebar (เช่น หน้าย่อย/หน้าเดิมของโมดูล) — ใช้ชื่อโมดูลจาก path ส่วนแรกแทน
  // (ปฏิทินที่ /profile ก็เข้าข่ายนี้) ไม่รู้จักโมดูลเลย = ไม่แสดงอะไร
  const segment = path.split('/')[1];
  const moduleName = Object.keys(SIDEBAR_MODULE_TITLE).find((name) => name.toLowerCase() === segment);

  return moduleName ? { href: `/${segment}`, title: SIDEBAR_MODULE_TITLE[moduleName], moduleName: null } : null;
};

// Header ด้านบนสุดของทุกหน้าหลัง login (ทุก layout + Home ใช้ component นี้ร่วมกัน)
// ซ้าย: ปุ่มเมนู (มือถือ) / กลาง: ชื่อหน้าที่เปิดอยู่ / ขวา: ปุ่ม Coreplan AI
// (ข้อมูลผู้ใช้ + ออกจากระบบอยู่ที่ MainSidebar)
export function HomeNavbar({ onMobileNavOpen }) {
  const location = useLocation();
  const pageLabel = getPageLabel(location.pathname);
  const me = useSelector((state) => state.me);
  const { toggle: toggleAiPanel } = useAiChatPanel();

  // ปุ่ม Coreplan AI ขึ้นให้ทุกคน (ข้อมูลที่ AI ตอบได้ถูกจำกัดตามสิทธิ์เมนูของผู้ถามที่ backend) — หน้าเต็มอยู่ที่ /profile/ai-chat

  const iconBtnSx = {
    color: 'rgba(255, 255, 255, 0.75)',
    '&:hover': {
      color: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
  };

  return (
    <Box
      component="nav"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        marginLeft: { lg: `${RAIL_WIDTH}px` },
        width: { lg: `calc(100% - ${RAIL_WIDTH}px)` },
        // ไล่สีบางๆ จากสีแถบซ้าย + เส้นขอบล่างสว่างจางๆ ให้ header ดูมีมิติ ไม่แบนทึบ
        background: `linear-gradient(90deg, ${BRAND_RAIL_BG} 0%, rgb(var(--color-primary-800)) 100%)`,
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        px: { xs: 1.5, lg: 3 },
        py: 0.75,
        // 3 โซน: ซ้าย | กลาง | ขวา — คอลัมน์ซ้าย/ขวากว้างเท่ากัน (1fr) เพื่อให้ชื่อหน้าตรงกลางอยู่กึ่งกลางจริง
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: 1,
        minHeight: 52,
      }}
    >
      {/* ซ้ายสุด: ปุ่มเมนู (มือถือเท่านั้น) */}
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        <IconButton color="inherit" onClick={onMobileNavOpen} sx={iconBtnSx}>
          <MenuIcon />
        </IconButton>
      </Box>

      {/* ตรงกลาง: ชื่อหน้าที่เปิดอยู่ — ชื่อโมดูลเล็กจางอยู่ก่อน ตามด้วยชื่อหน้าตัวใหญ่ขาว */}
      <Box
        sx={{
          gridColumn: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          minWidth: 0,
        }}
      >
        {pageLabel?.moduleName && (
          <>
            <Typography
              noWrap
              sx={{
                display: { xs: 'none', sm: 'block' },
                color: 'rgba(255, 255, 255, 0.55)',
                fontSize: '1rem',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {SIDEBAR_MODULE_TITLE[pageLabel.moduleName] || ''}
            </Typography>
            <ChevronRightIcon
              sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 18, color: 'rgba(255, 255, 255, 0.4)' }}
            />
          </>
        )}
        {pageLabel && (
          <Typography
            noWrap
            sx={{
              color: '#fff',
              fontSize: '1rem',
              fontWeight: 700,
              letterSpacing: 0.2,
              lineHeight: 1.2,
            }}
          >
            {pageLabel.title}
          </Typography>
        )}
      </Box>

      {/* ขวาสุด: ปุ่ม Coreplan AI — pill ไล่สีม่วง-น้ำเงิน เรืองแสงเมื่อ hover */}
      <Box sx={{ gridColumn: 3, display: 'flex', justifyContent: 'flex-end' }}>
        {Boolean(me?.userData) && (
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<AiIcon fontSize="small" />}
            onClick={toggleAiPanel}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 999,
              px: 2,
              color: '#fff',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 14px rgba(99, 102, 241, 0.45)',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c7ff5 0%, #9d6ff8 100%)',
                boxShadow: '0 6px 20px rgba(139, 92, 246, 0.6)',
                transform: 'translateY(-1px)',
              },
            }}
          >
            Coreplan AI
          </Button>
        )}
      </Box>
    </Box>
  );
}

HomeNavbar.propTypes = {
  onMobileNavOpen: PropTypes.func,
};

export default HomeNavbar;
